import { useApiLoader } from "../../../../../server/api/hooks/use-api-loader";
import { useSupabaseCallback } from "../../../../../server/supabase/hooks";
import { channelToFormat } from "../../../../../utils/channel-to-format";
import { SupabaseEmailDesignService } from "../../../../email-design/server/supabase-email-design-service";
import { useGetPollsWithOptions } from "../../../../poll/hooks/use-poll-with-options";
import {
  type SerialisedEditorDesign,
  isMarkdownDesign,
} from "../../types/serialised-editor-design";
import { type MessageBody, createMessageBody } from "./message-body";

import type { UseApiLoaderResponse } from "../../../../../server/api/hooks/use-api-loader";
import type { UUID } from "../../../../../utils/uuid";
import type { ChannelName } from "../../../../channels/types";
import type { EmailDesign } from "../../../../email-design/server/supabase-email-design-service";
import type { PollWithOptions } from "../../../../poll/server/supabase-poll-service";

interface Config {
  editorState: SerialisedEditorDesign | null;
  channel: ChannelName;
  account_id: UUID;
  email_design_id?: UUID;
  poll_ids?: UUID[];
  teams_poll_cards?: boolean;
}

type MessageBodyCreator = (config: Config) => Promise<
  | {
      data: MessageBody;
      errorMessage?: undefined;
    }
  | { data?: undefined; errorMessage?: string }
>;

/** `preloaded` is for a caller handed the design and polls already, and skips both reads. */
export const useMessageBodyCreator = (preloaded?: {
  email_design: EmailDesign | null;
  polls: PollWithOptions[] | null;
}): UseApiLoaderResponse<MessageBodyCreator> => {
  const getEmailDesign = useSupabaseCallback(
    async ({ supabase, account_id }, email_design_id?: UUID) => {
      const { data: emailDesignData, error } = await new SupabaseEmailDesignService(
        supabase,
      ).maybeGet(email_design_id ?? account_id);

      if (error) {
        throw new Error(`Failed to get email design. ${error?.message ?? ""}`);
      }
      return emailDesignData;
    },
    [],
  );

  const getPolls = useGetPollsWithOptions();

  return useApiLoader(
    async () =>
      async ({ editorState, channel, account_id, email_design_id, poll_ids, teams_poll_cards }) => {
        if (!editorState || isMarkdownDesign(editorState)) {
          return { data: { body: null, text: null } };
        }
        if (!channel) return { errorMessage: "Channel not supported" };
        const format = channelToFormat(channel);

        // Only loaded for the channels that use them, as they cost a round trip each.
        const [emailDesign, polls] = preloaded
          ? [
              preloaded.email_design ?? undefined,
              Object.fromEntries((preloaded.polls ?? []).map((poll) => [poll.id, poll])),
            ]
          : await Promise.all([
              format === "email" ? getEmailDesign(email_design_id) : undefined,
              poll_ids ? getPolls(poll_ids) : undefined,
            ]);

        const data = createMessageBody({
          design: editorState,
          channel,
          account_id,
          emailDesign: emailDesign ?? undefined,
          polls,
          teamsPollCards: teams_poll_cards,
        });

        return data ? { data } : { errorMessage: "Channel not supported" };
      },
    [getPolls, getEmailDesign, preloaded],
  );
};
